import { normalizeSubmissionShape } from '../services/submissionShape';
import type { ReportingSubmission } from '../services/dashboardData';
import { completenessFromUnknownRow } from './nursingComplianceScoring';
import {
  deriveSubmissionMetrics,
  METRICS_KEY_CHART1_CONSISTENCY_PCT,
  METRICS_KEY_MONTHLY_PCT,
  METRICS_KEY_OVERALL_CONSISTENCY_PCT,
  type SubmissionMetrics,
} from './submissionMetrics';

export type NurseNotesOfficialKpiId =
  | 'overallCompleteness'
  | 'diseaseCompleteness'
  | 'treatmentPlanConsistency';

export interface NurseNotesOfficialKpiDefinition {
  id: NurseNotesOfficialKpiId;
  label: string;
  shortLabel: string;
  target: number;
}

export interface NurseNotesKpiSummary {
  values: Record<NurseNotesOfficialKpiId, number | null>;
  cards: Array<NurseNotesOfficialKpiDefinition & { value: number | null; displayValue: string; statusLabel: string }>;
}

export const NURSE_NOTES_OFFICIAL_KPIS: readonly NurseNotesOfficialKpiDefinition[] = [
  {
    id: 'overallCompleteness',
    label: 'ร้อยละความสมบูรณ์ของบันทึกทางการพยาบาล ≥ 90',
    shortLabel: 'ความสมบูรณ์บันทึกทางการพยาบาล',
    target: 90,
  },
  {
    id: 'diseaseCompleteness',
    label: 'ร้อยละความสมบูรณ์ของบันทึกทางการพยาบาลรายโรค ≥ 90',
    shortLabel: 'ความสมบูรณ์รายโรค',
    target: 90,
  },
  {
    id: 'treatmentPlanConsistency',
    label: 'ร้อยละความสอดคล้องของบันทึกทางการพยาบาลกับแผนการรักษาของแพทย์ ≥ 90',
    shortLabel: 'ความสอดคล้องกับแผนการรักษา',
    target: 90,
  },
] as const;

export const NURSE_NOTES_DEFAULT_KPI_ID: NurseNotesOfficialKpiId = 'overallCompleteness';

export const NURSE_NOTES_KPI_LABEL_BY_ID = NURSE_NOTES_OFFICIAL_KPIS.reduce<
  Record<NurseNotesOfficialKpiId, NurseNotesOfficialKpiDefinition>
>((acc, kpi) => {
  acc[kpi.id] = kpi;
  return acc;
}, {
  overallCompleteness: NURSE_NOTES_OFFICIAL_KPIS[0],
  diseaseCompleteness: NURSE_NOTES_OFFICIAL_KPIS[1],
  treatmentPlanConsistency: NURSE_NOTES_OFFICIAL_KPIS[2],
});

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed && Number.isFinite(Number(trimmed))) return Number(trimmed);
  }
  return null;
};

const roundPct = (value: number): number => Math.round(value * 100) / 100;

const shouldExcludeOwnChartTitle = (title: string): boolean => {
  if (title.includes('ตรวจไขว้') || title.includes('แก้ไข')) return true;
  const lowered = title.toLowerCase();
  return lowered.includes('edit') || lowered.includes('audit') || lowered.includes('cross audit');
};

/** Same index set as `ReportingTable` / `deriveSubmissionMetrics` for own vs cross-audit rows. */
const buildIncludedChartIndexes = (
  answers: unknown[][],
  chartTitles: string[],
  isCrossAudit: boolean,
): number[] => {
  if (!isCrossAudit && chartTitles.length) {
    return answers.flatMap((_, idx) => {
      const title = typeof chartTitles[idx] === 'string' ? chartTitles[idx] : '';
      return title && !shouldExcludeOwnChartTitle(title) ? [idx] : [];
    });
  }
  return answers.map((_, idx) => idx);
};

const getSubmissionMetrics = (submission: ReportingSubmission): SubmissionMetrics => {
  const metrics = submission.metrics;
  if (metrics && typeof metrics === 'object' && !Array.isArray(metrics) && Object.keys(metrics).length > 0) {
    return metrics as SubmissionMetrics;
  }
  return deriveSubmissionMetrics(submission);
};

export function computeOverallCompletenessPct(submissions: readonly ReportingSubmission[]): number | null {
  let score = 0;
  let total = 0;
  const metricValues: number[] = [];

  for (const submission of submissions) {
    const metrics = getSubmissionMetrics(submission);
    const metricScore = toFiniteNumber(metrics.monthly_score);
    const metricTotal = toFiniteNumber(metrics.monthly_total);
    if (metricScore !== null && metricTotal !== null && metricTotal > 0) {
      score += metricScore;
      total += metricTotal;
      continue;
    }
    const value = toFiniteNumber(metrics[METRICS_KEY_MONTHLY_PCT]);
    if (value !== null) metricValues.push(value);
  }

  if (total > 0) return roundPct((score / total) * 100);
  if (metricValues.length > 0) return roundPct(metricValues.reduce((sum, value) => sum + value, 0) / metricValues.length);
  return null;
}

export function computeDiseaseCompletenessPct(submissions: readonly ReportingSubmission[]): number | null {
  const values: number[] = [];

  for (const submission of submissions) {
    const metrics = getSubmissionMetrics(submission);
    const storedPct = toFiniteNumber(metrics.disease_pct);
    if (storedPct !== null) {
      values.push(storedPct);
      continue;
    }

    const raw = submission as Record<string, unknown>;
    const shape = normalizeSubmissionShape(raw);
    const answers = shape.answers.map((row) => (Array.isArray(row) ? row : []));
    const chartTitles = shape.chartTitles.map((t) => (typeof t === 'string' ? t : String(t ?? '')));
    const isCrossAudit = !!(submission.isCrossAudit || raw.crossAudit === true);
    const includedIndexes = buildIncludedChartIndexes(answers, chartTitles, isCrossAudit);
    let score = 0;
    let full = 0;
    for (const rowIndex of includedIndexes) {
      const title = chartTitles[rowIndex] ?? '';
      if (!title.includes('รายโรค')) continue;
      const { score: rs, full: rf } = completenessFromUnknownRow(answers[rowIndex]);
      score += rs;
      full += rf;
    }
    if (full > 0) values.push(Math.round((score / full) * 10000) / 100);
  }

  if (values.length === 0) return null;
  return roundPct(values.reduce((sum, value) => sum + value, 0) / values.length);
}

export function computeTreatmentPlanConsistencyPct(submissions: readonly ReportingSubmission[]): number | null {
  const values = submissions.flatMap((submission) => {
    const metrics = getSubmissionMetrics(submission);
    const value =
      toFiniteNumber(metrics[METRICS_KEY_OVERALL_CONSISTENCY_PCT]) ??
      toFiniteNumber(metrics[METRICS_KEY_CHART1_CONSISTENCY_PCT]);
    return value === null ? [] : [value];
  });

  if (values.length === 0) return null;
  return roundPct(values.reduce((sum, value) => sum + value, 0) / values.length);
}

export function computeNurseNotesKpiValue(
  submissions: readonly ReportingSubmission[],
  kpiId: NurseNotesOfficialKpiId,
): number | null {
  switch (kpiId) {
    case 'overallCompleteness':
      return computeOverallCompletenessPct(submissions);
    case 'diseaseCompleteness':
      return computeDiseaseCompletenessPct(submissions);
    case 'treatmentPlanConsistency':
      return computeTreatmentPlanConsistencyPct(submissions);
  }
}

export function formatNurseNotesKpiValue(value: number | null): string {
  return value === null ? '-' : `${value.toFixed(2)}%`;
}

export function computeNurseNotesKpiSummary(
  submissions: readonly ReportingSubmission[],
): NurseNotesKpiSummary {
  const values: Record<NurseNotesOfficialKpiId, number | null> = {
    overallCompleteness: computeNurseNotesKpiValue(submissions, 'overallCompleteness'),
    diseaseCompleteness: computeNurseNotesKpiValue(submissions, 'diseaseCompleteness'),
    treatmentPlanConsistency: computeNurseNotesKpiValue(submissions, 'treatmentPlanConsistency'),
  };

  return {
    values,
    cards: NURSE_NOTES_OFFICIAL_KPIS.map((kpi) => {
      const value = values[kpi.id];
      return {
        ...kpi,
        value,
        displayValue: formatNurseNotesKpiValue(value),
        statusLabel:
          value !== null && value >= kpi.target
            ? 'ผ่านเกณฑ์'
            : value !== null
              ? 'ต่ำกว่าเกณฑ์'
              : 'ไม่มีข้อมูล',
      };
    }),
  };
}
